import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { AlertCircle, Clock, RotateCcw, UserCheck } from "lucide-react";

interface AdminCardProps {
  admin: {
    id: string;
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    created_at: string;
    status: "active" | "pending" | "expired" | string;
  };
  onResendInvitation: (userId: string) => void;
  isLoading?: boolean;
}

const AdminCard = ({
  admin,
  onResendInvitation,
  isLoading = false,
}: AdminCardProps) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <UserCheck className="w-4 h-4 text-green-500" />;
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case "expired":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "Registrado";
      case "pending":
        return "Pendiente";
      case "expired":
        return "Vencido";
      default:
        return status;
    }
  };

  return (
    <Card className="transition-shadow hover:shadow-md rounded-xl border border-gray-200">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="flex-1 space-y-1.5 gap-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {admin.first_name} {admin.last_name}
            </h3>

            <p className="text-sm text-gray-500">{admin.email}</p>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-700">
              <div>
                <span className="font-medium">Teléfono:</span>{" "}
                {admin.phone || (
                  <span className="text-gray-400">No especificado</span>
                )}
              </div>

              <div>
                <span className="font-medium">Creado:</span>{" "}
                {format(new Date(admin.created_at), "dd/MM/yy", {
                  locale: es,
                })}
              </div>

              <div className="flex items-center gap-2">
                {getStatusIcon(admin.status)}
                <span className="font-medium">
                  {getStatusText(admin.status)}
                </span>
              </div>
            </div>
          </div>

          {(admin.status === "pending" || admin.status === "expired") && (
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResendInvitation(admin.user_id)}
                disabled={isLoading}
                className="w-full md:w-auto"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {admin.status === "pending"
                  ? "Reenviar invitación"
                  : "Enviar nueva invitación"}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminCard;
